import React from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { t } from '@/shared/lib/i18n'
import { accessCategoriesProfilePath } from '@/shared/lib/routes'
import { AccessTree, useAccessTree } from '@/features/access-tree'
import type WebProfileAccessCategories from '@/shared/types/web/WebProfileAccessCategories'

interface AccessCategoriesSectionProps {
  accessCategories: WebProfileAccessCategories
}

export const AccessCategoriesSection = ({ accessCategories }: AccessCategoriesSectionProps) => {
  const { canEdit, sites, departments, units } = accessCategories

  const {
    tree,
    selectedUnitIds,
    toggleUnit,
    toggleDepartment,
    toggleSite,
    toggleAll,
    selectAllState,
    materialized,
    computeDepartmentState,
    computeSiteState,
  } = useAccessTree(sites, departments, units)

  const form = useForm({
    siteIds: materialized.siteIds,
    departmentIds: materialized.departmentIds,
    unitIds: materialized.unitIds,
  })

  React.useEffect(() => {
    form.setData({
      siteIds: materialized.siteIds,
      departmentIds: materialized.departmentIds,
      unitIds: materialized.unitIds,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialized])

  return (
    <section className="space-y-4 rounded-lg border p-6">
      <div>
        <h2 className="text-lg font-semibold">{t('profile.accessCategories.sectionTitle')}</h2>
        <p className="text-sm text-muted-foreground">
          {canEdit
            ? t('profile.accessCategories.sectionDescriptionEditable')
            : t('profile.accessCategories.sectionDescriptionReadOnly')}
        </p>
      </div>

      <form
        onSubmit={(formEvent) => {
          formEvent.preventDefault()
          form.patch(accessCategoriesProfilePath())
        }}
        className="space-y-3"
      >
        <AccessTree
          sites={sites}
          departments={departments}
          units={units}
          tree={tree}
          selectedUnitIds={selectedUnitIds}
          toggleUnit={toggleUnit}
          toggleDepartment={toggleDepartment}
          toggleSite={toggleSite}
          toggleAll={toggleAll}
          selectAllState={selectAllState}
          computeDepartmentState={computeDepartmentState}
          computeSiteState={computeSiteState}
          materialized={materialized}
          readOnly={!canEdit}
        />

        {!canEdit && <NoAccessNotice />}

        {canEdit && (
          <Button type="submit" disabled={form.processing}>
            {t('profile.accessCategories.saveButton')}
          </Button>
        )}
      </form>
    </section>
  )
}
